// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRef } from 'react';
/**
 * Provides imperative focus management for the inline token list.
 *
 * `focusTokenAtIndex` translates an absolute token index (0-based within the full
 * token array) to a position-in-visible index, then focuses the corresponding dismiss
 * button. If the token is not visible (index falls before the visible window), focus
 * is sent to the input instead.
 *
 * The snapshot ref is updated on every render so that keyboard handlers, which close
 * over a stale `effectiveVisible`, always see the latest value.
 */
export function useTokenListFocus({ tokenListRef, inputRef, tokenListLength, effectiveVisible, }) {
    const snapshotRef = useRef({ tokenListLength, effectiveVisible });
    // Mutate every render so keyboard-handler callbacks capture the live values.
    snapshotRef.current = { tokenListLength, effectiveVisible };
    const focusTokenAtIndex = (absoluteIndex) => {
        var _a, _b;
        const { tokenListLength: len, effectiveVisible: ev } = snapshotRef.current;
        const visibleStart = len - ev;
        const positionInVisible = absoluteIndex - visibleStart;
        if (positionInVisible < 0) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        const dismissButtons = (_b = tokenListRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('[data-token-item] button');
        if (dismissButtons && dismissButtons[positionInVisible]) {
            dismissButtons[positionInVisible].focus();
        }
    };
    return { focusTokenAtIndex };
}
//# sourceMappingURL=use-token-list-focus.js.map